import { Upload, Button } from 'antd';
import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { DraggerProps } from 'antd/lib/upload';
import Form, { FormItemProps } from 'antd/lib/form';
import { WrappedFieldProps } from 'redux-form';
import { CustomIcon } from 'components/custom-icon/CustomIcon';
import { UPLOAD_FILE_BACKGROUND_SVG } from 'assets/icons/icons';
import { Text } from 'components/text/Text';
import { colors } from 'styles/colors';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { Stack } from 'components/stack/stack';
import { FormPhotoValue, UPLOAD_IMAGE_ACCEPTED_FORMAT } from '../offer-form.logic';
import { ImageFitContain } from 'src/pages-all/details/tutor/components/commons';

function getBase64(img: Blob, callback: (imageUrl: string) => void) {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
}

const { Dragger } = Upload;

const UploadNewStyles = styled.div`
  text-decoration: underline;
  text-decoration-color: ${colors.primary};
  &:hover {
    cursor: pointer;
  }
`;

const UploaderStyles = styled.div`
  ${tw`flex items-center`}
  flex: 1;
  background: white;
  .ant-upload.ant-upload-drag {
    background: white;
    border: 4px dashed #bbbbbb;
    border-radius: 10px;
  }
  .ant-upload-text {
    display: none;
  }
  .ant-upload-hint {
    display: none;
  }
  .ant-upload.ant-upload-drag {
    ${({ isSelected }: { isSelected: boolean }) =>
      isSelected
        ? css`
            display: none;
          `
        : ''}
  }
  ${ImageFitContain} {
    background: white;
  }
`;

const BackgroundImage = styled(CustomIcon)`
  margin-right: 16px;
  margin-left: 16px;
  width: calc(100% - 32px);
`;

const UploadButton = styled(Button)`
  max-width: 460px;
`;

const UploadButtonPositioner = styled.div`
  position: absolute;
  top: calc(50% - 20px);
  left: calc(50% - 70px);
`;

const Centered = styled.div`
  ${tw`flex flex-1 items-center justify-center`}
  .ant-form-item {
    ${tw`flex flex-1`}
  }
`;

type UploadInputProps = WrappedFieldProps & FormItemProps;

export const UploadInput: FC<UploadInputProps> = ({ meta, input }) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const errorMessage = !!meta && meta.error;
  const showError = !!meta && meta.touched && !!meta.error;
  const isImageTooBig = meta && meta.error && !!meta.error.match(/6 MB/);
  const { onChange } = input;
  const value = input.value as FormPhotoValue;
  const file = value?.file as any;
  const url = value?.url;
  const isPictureDefault = !!(url && url.match(/\/profile\/default\.jpg/));
  const isSelected = !!file && !!previewUrl;
  const isAnyImage = (!!isSelected || !!url) && !isImageTooBig && !isPictureDefault;

  const handleBeforeUpload = () => false;

  const handleOnChange: DraggerProps['onChange'] = (info) => {
    const newValue = info.fileList.pop()?.originFileObj;
    getBase64(newValue as File, (imageUrl) => setPreviewUrl(imageUrl));
    onChange({ file: newValue });
  };

  const handleClear = () => {
    onChange(null);
    setPreviewUrl(undefined);
  };

  const getValue = () => (file ? [file] : []);

  const renderPreview = (url: string | undefined) => (
    <>
      <ImageFitContain
        width={'auto'}
        height={370}
        style={{ maxHeight: '370px', overflow: 'hidden' }}
        src={url}
        preview={false}
      />
      <UploadNewStyles onClick={handleClear}>
        <Text size={14} color="primary">
          {translate(translationKeys.publishOffer.photo.uploadNewPicture)}
        </Text>
      </UploadNewStyles>
    </>
  );

  return (
    <Centered>
      <Form.Item
        validateStatus={showError ? 'error' : undefined}
        help={showError ? errorMessage : undefined}
      >
        {!isPictureDefault && url && !previewUrl ? renderPreview(url) : null}
        {previewUrl
          ? !isImageTooBig && !isPictureDefault
            ? renderPreview(previewUrl)
            : null
          : null}
        <UploaderStyles isSelected={isAnyImage}>
          <Dragger
            showUploadList={false /* We show preview manually */}
            multiple={false}
            fileList={getValue()}
            accept={UPLOAD_IMAGE_ACCEPTED_FORMAT}
            beforeUpload={handleBeforeUpload}
            onChange={handleOnChange}
            itemRender={() => null /* We show preview manually */}
          >
            {!isAnyImage ? (
              <div>
                <BackgroundImage src={UPLOAD_FILE_BACKGROUND_SVG} />
                <UploadButtonPositioner>
                  <Stack flow="row">
                    <UploadButton type={'primary'} className={'form-button'} htmlType="button">
                      {translate(translationKeys.publishOffer.photo.uploadPhoto)}
                    </UploadButton>
                    <Text size={14} color="grey">
                      {translate(translationKeys.publishOffer.photo.dragPhoto)}
                    </Text>
                  </Stack>
                </UploadButtonPositioner>
              </div>
            ) : null}
          </Dragger>
        </UploaderStyles>
      </Form.Item>
    </Centered>
  );
};
